import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Paper, Table, TableBody,
    TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography, Autocomplete,
} from '@mui/material';
import { ItemCustodyService } from '../../services/itemCustody.service';
import { ItemService } from '../../services/item.service';
import { DepartmentService } from '../../services/department.service';
import { getStoresByInstitutionId } from '../../services/store.service';
import { ItemCustody, CustodyStatus } from '../../interfaces/item-custody.interface';
import { Item } from '../../interfaces/item.interface';
import { Department } from '../../interfaces/department.interface';
import { Store } from '../../interfaces/store.interface';
import { EmployeeAutocomplete, EmployeeOption } from '../common/EmployeeAutocomplete';
import SearchableSelect from '../common/SearchableSelect';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';

const STATUS_LABEL: Record<CustodyStatus, string> = { OPEN: 'Open', RETURNED: 'Returned', WRITTEN_OFF: 'Written off' };
const STATUS_COLOR: Record<CustodyStatus, 'warning' | 'success' | 'default'> = { OPEN: 'warning', RETURNED: 'success', WRITTEN_OFF: 'default' };

type ActionKind = 'return' | 'writeOff';

/** Items out with people: returnable items issued to an employee or a department, with Return and Write off. */
const ItemCustodyList: React.FC = () => {
    const toast = useToast();
    const canIssue = usePermission(PERMISSIONS.STOCK_ISSUE);
    const [rows, setRows] = useState<ItemCustody[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [total, setTotal] = useState(0);

    const [status, setStatus] = useState<CustodyStatus | ''>('OPEN');
    const [itemId, setItemId] = useState('');
    const [holder, setHolder] = useState<EmployeeOption | null>(null);
    const [departmentId, setDepartmentId] = useState('');
    const [storeId, setStoreId] = useState('');

    const [items, setItems] = useState<Item[]>([]);
    const [departments, setDepartments] = useState<Department[]>([]);
    const [stores, setStores] = useState<Store[]>([]);

    const [action, setAction] = useState<{ kind: ActionKind; row: ItemCustody } | null>(null);
    const [qty, setQty] = useState('');
    const [text, setText] = useState('');
    const [saving, setSaving] = useState(false);
    const [serials, setSerials] = useState<string[]>([]);
    const [itemSearch, setItemSearch] = useState('');
    const [selectedItem, setSelectedItem] = useState<Item | null>(null);
    const loadSeq = useRef(0);

    useEffect(() => {
        DepartmentService.getLocationOptions().then(r => setDepartments(r.content ?? [])).catch(() => setDepartments([]));
        // Same source as Stock List: the stores this user can see (server-scoped), not only active ones
        getStoresByInstitutionId(0, 100, 'ALL').then(r => setStores(r.content)).catch(() => setStores([]));
    }, []);

    // Item filter searches on the server as the user types (no fixed 100-item cap)
    useEffect(() => {
        let cancelled = false;
        const handle = setTimeout(() => {
            ItemService.getItemsByInstitution(0, 30, itemSearch, 'STOCK', undefined, 'RETURNABLE')
                .then(r => { if (!cancelled) setItems(r.content); })
                .catch(() => { if (!cancelled) setItems([]); });
        }, 250);
        return () => { cancelled = true; clearTimeout(handle); };
    }, [itemSearch]);

    const load = useCallback(async () => {
        const seq = ++loadSeq.current;   // a slower, older response must not overwrite a newer one
        try {
            setLoading(true);
            const r = await ItemCustodyService.list(
                { status, itemId, holderUserId: holder?.id, departmentId, storeId }, page, rowsPerPage);
            if (seq !== loadSeq.current) return;
            setRows(r.content);
            setTotal(r.totalElements);
        } catch (err) {
            if (seq !== loadSeq.current) return;
            toast.showApiError(err, 'Failed to load items out with people');
        } finally {
            if (seq === loadSeq.current) setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, itemId, holder, departmentId, storeId, page, rowsPerPage]);

    useEffect(() => { load(); }, [load]);

    const openAction = (kind: ActionKind, row: ItemCustody) => {
        setAction({ kind, row });
        setQty(String(row.outQty));
        setText('');
        setSerials([]);
    };

    const qtyNumber = Number(qty);
    const qtyValid = Number.isInteger(qtyNumber) && qtyNumber > 0 && !!action && qtyNumber <= action.row.outQty;
    const serialTracked = !!action && (action.row.outSerials?.length ?? 0) > 0;
    const serialsValid = !serialTracked || serials.length === qtyNumber;
    const canSubmit = qtyValid && serialsValid && (action?.kind !== 'writeOff' || text.trim().length > 0) && !saving;

    const submit = async () => {
        if (!action || !canSubmit) return;
        try {
            setSaving(true);
            if (action.kind === 'return') {
                await ItemCustodyService.returnToStore(action.row.id, qtyNumber, text.trim() || undefined, serialTracked ? serials : undefined);
                toast.showSuccess('Returned to the store');
            } else {
                await ItemCustodyService.writeOff(action.row.id, qtyNumber, text.trim(), serialTracked ? serials : undefined);
                toast.showSuccess('Written off');
            }
            setAction(null);
            await load();
        } catch (err) {
            const denied = (err as { response?: { status?: number } })?.response?.status === 403;
            if (denied) {
                toast.showError('You are not in charge of this store, so you cannot return or write off items from it.');
            } else {
                toast.showApiError(err, action.kind === 'return' ? 'Failed to return the item' : 'Failed to write off');
            }
        } finally {
            setSaving(false);
        }
    };

    const resetPage = () => setPage(0);

    return (
        <Box sx={{ p: 3 }} data-help-page="store.custody.page">
            <Typography variant="h5" sx={{ mb: 2 }}>Items out with people</Typography>

            <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }} data-help-section="store.custody.filter">
                <TextField select size="small" label="Status" data-help="store.custody.filter.status"
                    sx={{ flex: '1 1 140px', maxWidth: 200 }} value={status}
                    onChange={(e) => { setStatus(e.target.value as CustodyStatus | ''); resetPage(); }}
                    data-testid="custody-status-filter">
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="OPEN">Open</MenuItem>
                    <MenuItem value="RETURNED">Returned</MenuItem>
                    <MenuItem value="WRITTEN_OFF">Written off</MenuItem>
                </TextField>
                <SearchableSelect<Item>
                    options={items}
                    value={itemId}
                    selectedOption={selectedItem}
                    disableClientFilter
                    onInputChange={setItemSearch}
                    onChange={(id, opt) => { setItemId(id); setSelectedItem(opt); resetPage(); }}
                    getOptionLabel={(i) => `${i.itemName} (${i.itemCode})`}
                    data-help="store.custody.filter.item" label="Item"
                    clearable
                    fullWidth={false}
                    sx={{ flex: '1 1 220px', maxWidth: 320 }}
                    data-testid="custody-item-filter"
                />
                <EmployeeAutocomplete
                    label="Held by"
                    data-help="store.custody.filter.holder"
                    value={holder}
                    onChange={(v) => { setHolder(v); resetPage(); }}
                    onError={(error) => toast.showApiError(error, 'Failed to search employees')}
                    sx={{ flex: '1 1 220px', maxWidth: 320 }}
                />
                <TextField select size="small" label="Department" data-help="store.custody.filter.department"
                    sx={{ flex: '1 1 180px', maxWidth: 260 }} value={departmentId}
                    onChange={(e) => { setDepartmentId(e.target.value); resetPage(); }}
                    data-testid="custody-department-filter">
                    <MenuItem value="">All departments</MenuItem>
                    {departments.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Store" data-help="store.custody.filter.store"
                    sx={{ flex: '1 1 180px', maxWidth: 260 }} value={storeId}
                    onChange={(e) => { setStoreId(e.target.value); resetPage(); }}
                    data-testid="custody-store-filter">
                    <MenuItem value="">All stores</MenuItem>
                    {stores.map(s => <MenuItem key={s.id} value={s.id}>{s.storeName}</MenuItem>)}
                </TextField>
            </Box>

            <TableContainer component={Paper} data-help-section="store.custody.table">
                <Table size="small" sx={{ minWidth: 720 }} data-testid="custody-table">
                    <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5', '& th': { fontWeight: 'bold' } }}>
                            <TableCell data-help="store.custody.col.item">Item</TableCell>
                            <TableCell data-help="store.custody.col.holder">Held by</TableCell>
                            <TableCell data-help="store.custody.col.department">Department</TableCell>
                            <TableCell data-help="store.custody.col.store">Store</TableCell>
                            <TableCell data-help="store.custody.col.issued">Issued on</TableCell>
                            <TableCell data-help="store.custody.col.qty" align="right">Out</TableCell>
                            <TableCell data-help="store.custody.col.status">Status</TableCell>
                            <TableCell data-help="store.custody.col.actions">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && (
                            <TableRow><TableCell colSpan={8} align="center" data-testid="custody-loading">Loading...</TableCell></TableRow>
                        )}
                        {!loading && rows.length === 0 && (
                            <TableRow><TableCell colSpan={8} align="center">Nothing is out with anyone.</TableCell></TableRow>
                        )}
                        {!loading && rows.map(r => (
                            <TableRow key={r.id} hover data-testid={`custody-row-${r.id}`}>
                                <TableCell>
                                    {r.itemName}
                                    {r.itemCode && <Typography component="div" variant="caption" color="text.secondary">{r.itemCode}</Typography>}
                                </TableCell>
                                <TableCell>{r.holderName || '-'}</TableCell>
                                <TableCell>{r.holderDepartmentName || '-'}</TableCell>
                                <TableCell>{r.storeName}</TableCell>
                                <TableCell>{new Date(r.issuedAt).toLocaleString()}</TableCell>
                                <TableCell align="right">
                                    {r.outQty}{r.unit ? ` ${r.unit}` : ''}
                                    <Typography component="div" variant="caption" color="text.secondary">of {r.issuedQty} issued</Typography>
                                </TableCell>
                                <TableCell><Chip size="small" color={STATUS_COLOR[r.status]} label={STATUS_LABEL[r.status]} /></TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    {canIssue && r.status === 'OPEN' && (
                                        <>
                                            <Button size="small" data-help="store.custody.action.return" onClick={() => openAction('return', r)}>Return</Button>
                                            <Button size="small" color="warning" data-help="store.custody.action.writeOff" onClick={() => openAction('writeOff', r)}>Write off</Button>
                                        </>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[10, 25, 50]}
                    onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
                />
            </TableContainer>

            <Dialog open={!!action} onClose={() => setAction(null)} fullWidth maxWidth="xs">
                <DialogTitle>{action?.kind === 'return' ? 'Return to store' : 'Write off'}</DialogTitle>
                <DialogContent>
                    {action && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {action.row.itemName}, {action.row.outQty} still out
                            {action.kind === 'return' ? `. It goes back to ${action.row.storeName}.` : '. Nothing goes back to stock.'}
                        </Typography>
                    )}
                    <TextField fullWidth margin="normal" type="number" label="Quantity" data-help="store.custody.field.qty"
                        value={qty} onChange={(e) => setQty(e.target.value)}
                        error={!!qty && !qtyValid}
                        helperText={!!qty && !qtyValid ? `Enter a whole number from 1 to ${action?.row.outQty}` : undefined}
                        inputProps={{ min: 1, max: action?.row.outQty }} data-testid="custody-qty" />
                    {serialTracked && action && (
                        <Autocomplete<string, true>
                            multiple
                            options={action.row.outSerials ?? []}
                            value={serials}
                            onChange={(_, v) => setSerials(v)}
                            renderInput={(params) => (
                                <TextField {...params} margin="normal" label={`Serial numbers (pick ${qtyValid ? qtyNumber : '...'})`}
                                    data-help="store.custody.field.serials"
                                    error={serials.length > 0 && !serialsValid}
                                    helperText={serials.length > 0 && !serialsValid ? `Pick exactly ${qtyNumber}` : undefined} />
                            )}
                            data-testid="custody-serials"
                        />
                    )}
                    {action?.kind === 'return' ? (
                        <TextField fullWidth margin="normal" multiline rows={2} label="Remarks (optional)"
                            data-help="store.custody.field.remarks"
                            value={text} onChange={(e) => setText(e.target.value)}
                            inputProps={{ maxLength: 500 }} data-testid="custody-text" />
                    ) : (
                        <TextField fullWidth margin="normal" multiline rows={2} label="Reason" required
                            data-help="store.custody.field.reason"
                            value={text} onChange={(e) => setText(e.target.value)}
                            inputProps={{ maxLength: 500 }} data-testid="custody-text" />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAction(null)}>Cancel</Button>
                    <Button variant="contained" disabled={!canSubmit} onClick={submit} data-testid="custody-confirm">
                        {action?.kind === 'return' ? 'Return' : 'Write off'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ItemCustodyList;
